/**
 * Softphone du CRM — indépendant du fournisseur téléphonique.
 *
 * Ce contexte portait le nom et l'implémentation de Twilio (`TwilioVoice.jsx`)
 * alors que l'API qu'il expose est déjà générique. La logique — reconnexion,
 * veille, sonnerie, appel en cours — n'a rien de propre à un opérateur ; seule
 * la liaison l'est. Elle vit maintenant dans un **pilote**, choisi d'après ce
 * que le backend annonce (`driver`), et l'interface ne teste jamais le nom du
 * fournisseur.
 *
 * `@/context/TwilioVoice` reste servi comme alias : les composants existants
 * (`useTwilio`, `TwilioProvider`) continuent de fonctionner sans modification.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/crmApi";
import { useAuth } from "@/context/CrmAuth";
import { loadDriver } from "@/context/telephony/drivers";
import { dialProblem, toE164 } from "@/lib/phone";
import { startRingback, stopRingback } from "@/lib/ringback";

/** Libellé humain de l'état d'un appel sortant avant le décroché. */
export function dialLabel(state) {
  if (!state) return "Connexion…";
  const st = (state.call_status || "").toLowerCase();
  if (state.stage === "answered" || st === "in-progress" || st === "answered") return "En ligne";
  if (st === "busy") return "Occupé";
  if (st === "no-answer") return "Pas de réponse";
  if (st === "failed" || st === "canceled") return `Échec de l'appel${state.sip_code ? ` (code ${state.sip_code})` : ""}`;
  if (state.stage === "ringing" || st === "ringing") return state.earlyMedia === false ? "Ça sonne (sans tonalité)" : "Ça sonne…";
  if (st === "initiated" || st === "queued") return "Numérotation…";
  return "Connexion…";
}

// 31005 « Error sent from gateway in HANGUP » : le SDK a bien joint Twilio,
// mais Twilio n'a pas obtenu du CRM les instructions de l'appel (webhook
// injoignable, signature refusée, TwiML en erreur). Le code seul ne dit rien
// à l'agent ; le serveur, lui, a mémorisé le dernier webhook refusé.
const GATEWAY_CODES = new Set([31005, 31000, 31002]);
export function isGatewayError(e) {
  const code = Number(e?.code);
  const msg = String(e?.message || e || "");
  return GATEWAY_CODES.has(code) || /gateway/i.test(msg);
}

async function explainCallError(e) {
  const msg = e?.message || String(e);
  if (!isGatewayError(e)) { toast.error(`Erreur d'appel : ${msg}`); return; }
  toast.error(`Erreur d'appel : ${msg}`, { id: "tw-gateway" });
  try {
    const { data } = await api.get("/twilio/status");
    const cause = data?.last_webhook_error;
    const quand = data?.last_webhook_error_at ? new Date(data.last_webhook_error_at).toLocaleString("fr-FR") : null;
    // Deux familles de causes, que le serveur départage : un webhook refusé
    // (il en garde la trace) ; sinon, presque toujours un numéro que
    // l'opérateur ne peut pas composer — invalide ou non attribué (Twilio 13224).
    toast.error(cause
      ? `Twilio n'a pas pu obtenir les instructions du CRM. Dernier webhook refusé${quand ? ` (${quand})` : ""} : ${cause}`
      : "L'opérateur a raccroché avant la mise en relation. Aucun webhook n'a été refusé côté CRM : "
        + "le numéro composé est probablement invalide ou non attribué (code Twilio 13224). "
        + "Vérifiez le numéro sur la fiche ; l'administrateur peut confirmer dans Paramètres › Téléphonie › Incidents Twilio.",
      { id: "tw-gateway-cause", duration: 15000 });
  } catch { /* le premier message suffit */ }
}

const TelephonyCtx = createContext(null);
export const useTelephony = () => useContext(TelephonyCtx);

function playWarningSound() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    [0, 0.35, 0.7].forEach((t) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "square";
      o.frequency.setValueAtTime(740, ctx.currentTime + t);
      o.frequency.setValueAtTime(520, ctx.currentTime + t + 0.12);
      g.gain.setValueAtTime(0.12, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.25);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + t);
      o.stop(ctx.currentTime + t + 0.28);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1500);
  } catch { /* audio bloqué par le navigateur */ }
}

// Reconnexion : l'ancienne boucle réessayait toutes les 5 s, indéfiniment et
// dans chaque onglet ouvert. Une panne d'une nuit produisait ainsi des milliers
// d'enregistrements par poste — exactement le profil de trafic qu'un opérateur
// signale comme anormal. Palier exponentiel, plafonné, borné en nombre de
// tentatives, et suspendu quand l'onglet est en arrière-plan.
const RETRY_BASE_MS = 5000;
const RETRY_MAX_MS = 300000;
const RETRY_MAX_ATTEMPTS = 12;

// Déconnexion volontaire du fournisseur téléphonique : mémorisée PAR UTILISATEUR
// pour ne pas être redemandée à chaque chargement de page. La clé porte l'id du
// compte, sinon un admin qui emprunte l'identité d'un commercial hériterait de
// son choix. localStorage peut lever (navigation privée) → on retombe alors sur
// le comportement par défaut : connexion automatique.
const OFFLINE_KEY = "prospee_tw_offline";
const readManualOffline = (userId) => {
  try { return localStorage.getItem(`${OFFLINE_KEY}:${userId}`) === "1"; } catch { return false; }
};
const persistManualOffline = (userId, value) => {
  if (!userId) return;
  try {
    if (value) localStorage.setItem(`${OFFLINE_KEY}:${userId}`, "1");
    else localStorage.removeItem(`${OFFLINE_KEY}:${userId}`);
  } catch { /* non bloquant */ }
};

// status: unconfigured | offline | connecting | reconnecting | ready | error
export function TelephonyProvider({ children }) {
  const { user } = useAuth();
  const driverRef = useRef(null);
  const activeRef = useRef(null);
  const incomingRef = useRef(null);
  const timerRef = useRef(null);
  const refRef = useRef(null);
  // Le correspondant a-t-il décroché ? Posé par « accept », relayé dans
  // `endedCall`. Un appel sans réponse ou en échec n'a ni durée ni
  // conversation : ce qui dépend de l'appel (copilote, qualification) doit
  // pouvoir le savoir sans le deviner à partir du chronomètre.
  const answeredRef = useRef(false);
  // Miroirs des états lus à la fin d'un appel. Les gestionnaires posés par
  // `bindCall` sont figés au moment de la composition : y lire `activeInfo` ou
  // `callSeconds` directement donnait la valeur d'AVANT l'appel — donc aucune
  // fiche à qualifier pour un appel sortant, et une durée toujours nulle.
  const activeInfoRef = useRef(null);
  const callSecondsRef = useRef(0);

  const [status, setStatus] = useState("offline");
  // Fournisseur actif + capacités, annoncés par le backend. L'interface s'y
  // réfère au lieu de tester un nom d'opérateur.
  const [providerInfo, setProviderInfo] = useState(null);
  const manualOfflineRef = useRef(false);
  const retryRef = useRef(null);
  const lostRef = useRef(false);
  const reconnectFnRef = useRef(null);
  const reconnectBusyRef = useRef(false);
  const statusRef = useRef("offline");
  const connectingSinceRef = useRef(0);

  useEffect(() => { statusRef.current = status; }, [status]);
  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [callSeconds, setCallSeconds] = useState(0);
  const [activeInfo, setActiveInfo] = useState(null); // {direction, number, company, contact, meta}
  const [incomingInfo, setIncomingInfo] = useState(null); // {number, caller_type, company, contact}
  const [endedCall, setEndedCall] = useState(null); // {ref, number, company_id, company_name, direction, seconds, answered}
  const [dialState, setDialState] = useState(null); // {stage, earlyMedia, call_status, sip_code} avant le décroché

  const retryAttemptRef = useRef(0);
  useEffect(() => { activeInfoRef.current = activeInfo; }, [activeInfo]);
  useEffect(() => { callSecondsRef.current = callSeconds; }, [callSeconds]);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  const stopRetry = () => { if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; } };
  const startTimer = () => {
    stopTimer();
    setCallSeconds(0);
    timerRef.current = setInterval(() => setCallSeconds((s) => s + 1), 1000);
  };

  const teardownActive = useCallback((seconds) => {
    stopTimer();
    stopRingback();
    // Un appel non décroché laissait le statut sur « connecting » : le watchdog
    // finissait par le prendre pour une panne quinze secondes plus tard.
    if (!manualOfflineRef.current && driverRef.current?.registered) {
      setStatus("ready");
      lostRef.current = false;
    }
    const info = activeInfoRef.current;
    if (info && (info.company_id || info.number)) {
      setEndedCall({
        ref: refRef.current, number: info.number, company_id: info.company_id,
        company_name: info.company_name, contact_id: info.contact_id,
        direction: info.direction, seconds: seconds ?? callSecondsRef.current,
        answered: answeredRef.current,
      });
    }
    activeRef.current = null;
    refRef.current = null;
    answeredRef.current = false;
    setActiveInfo(null);
    setDialState(null);
    setMuted(false);
    setHeld(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bindCall = useCallback((call) => {
    call.on("accept", () => { stopRingback(); answeredRef.current = true; setStatus("ready"); setDialState({ stage: "answered" }); startTimer(); });
    // Le SDK annonce la sonnerie côté correspondant. Sans « early media »,
    // aucun son n'arrive de l'opérateur : on joue nous-mêmes la tonalité de
    // retour d'appel, comme Twilio le recommande (lib/ringback).
    call.on("ringing", (hasEarlyMedia) => {
      setDialState((d) => ({ ...(d || {}), stage: "ringing", earlyMedia: hasEarlyMedia }));
      if (hasEarlyMedia === false) startRingback();
    });
    call.on("disconnect", () => teardownActive(callSecondsRef.current));
    call.on("cancel", () => teardownActive(0));
    call.on("reject", () => teardownActive(0));
    call.on("error", (e) => { explainCallError(e); teardownActive(0); });
  }, [teardownActive]);

  const scheduleRetry = useCallback(() => {
    if (retryRef.current || manualOfflineRef.current) return;
    if (retryAttemptRef.current >= RETRY_MAX_ATTEMPTS) {
      // On rend la main plutôt que de marteler : l'utilisateur relance quand
      // il le veut, via « Se connecter » (visible en statut "offline").
      setStatus("offline");
      toast.error("Reconnexion abandonnée après plusieurs tentatives. "
        + "Cliquez sur « Se connecter » pour réessayer.", { id: "tw-signal", duration: 15000 });
      return;
    }
    const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** retryAttemptRef.current);
    retryAttemptRef.current += 1;
    retryRef.current = setTimeout(() => {
      retryRef.current = null;
      // Onglet en arrière-plan : inutile de rouvrir une liaison que personne
      // ne regarde — on repousse au palier suivant.
      if (typeof document !== "undefined" && document.hidden) { scheduleRetry(); return; }
      reconnectFnRef.current?.();
    }, delay);
  }, []);

  const handleSignalLost = useCallback((reason) => {
    if (manualOfflineRef.current) { setStatus("offline"); return; }
    // Communication en cours : on ne relance rien. Se reconnecter impose de
    // détruire l'appareil, ce qui raccrocherait l'appel que l'utilisateur est
    // en train de passer — le SDK, lui, rétablit son média tout seul. On
    // signale sobrement, et la reconnexion attendra la fin de l'appel.
    if (activeRef.current) {
      toast.warning(`⚠ Liaison instable${reason ? ` — ${reason}` : ""}`,
        { id: "tw-signal", duration: 6000 });
      return;
    }
    if (!lostRef.current) playWarningSound();
    lostRef.current = true;
    setStatus("reconnecting");
    toast.warning(`⚠ Signal téléphonique perdu${reason ? ` — ${reason}` : ""}. Reconnexion automatique en cours…`,
      { id: "tw-signal", duration: 10000 });
    scheduleRetry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleRetry]);

  const refreshToken = useCallback(async () => {
    try {
      const { data } = await api.get("/telephony/token");
      await driverRef.current?.updateToken(data);
    } catch { /* ignore */ }
  }, []);

  const handleIncoming = useCallback(async (call, from) => {
    incomingRef.current = call;
    let info = { number: from, caller_type: "NOUVEAU", company: null, contact: null };
    try {
      const { data } = await api.get(`/telephony/lookup?number=${encodeURIComponent(from)}`);
      info = { ...info, ...data };
    } catch { /* ignore */ }
    setIncomingInfo(info);
    call.on("cancel", () => { incomingRef.current = null; setIncomingInfo(null); });
    call.on("disconnect", () => teardownActive(callSecondsRef.current));
    call.on("reject", () => { incomingRef.current = null; setIncomingInfo(null); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teardownActive]);

  const initialize = useCallback(async () => {
    if (!user || user.telephony_enabled === false) return;
    manualOfflineRef.current = false;
    connectingSinceRef.current = Date.now();
    setStatus((s) => (s === "reconnecting" ? s : "connecting"));
    // Un pilote non détruit garde sa liaison ouverte : sans ça, chaque
    // « Se connecter » ajoutait un enregistrement de plus sur la même
    // identité, et les appels entrants sonnaient en double.
    const ancien = driverRef.current;
    driverRef.current = null;
    ancien?.destroy?.();
    try {
      const { data } = await api.get("/telephony/token");
      // Le pilote est chargé à la demande : un poste ne télécharge que le SDK
      // du fournisseur qu'il utilise réellement.
      const Driver = await loadDriver(data.driver);
      if (!Driver) {
        // Le backend annonce un pilote que cette version du frontend n'embarque
        // pas : le dire, plutôt que d'échouer en silence sur un bouton mort.
        console.warn(`[telephonie] pilote « ${data.driver} » non embarqué par ce frontend`);
        setStatus("unconfigured");
        return;
      }
      // Un pilote que l'on a détruit nous-mêmes (déconnexion, changement de
      // compte, reconnexion) émet encore « désenregistré » : ce n'est pas une
      // perte de signal. Seul le pilote courant a la parole.
      const courant = () => driverRef.current === driver;
      const driver = new Driver({
        onIncoming: handleIncoming,
        onRegistered: () => {
          if (!courant()) return;
          stopRetry();
          retryAttemptRef.current = 0;
          if (lostRef.current) {
            lostRef.current = false;
            toast.success("Signal rétabli — téléphone de nouveau opérationnel", { id: "tw-signal" });
          }
          setStatus("ready");
        },
        onUnregistered: () => { if (courant()) handleSignalLost(); },
        onTokenExpiring: refreshToken,
        onError: (message) => { if (courant()) handleSignalLost(message); },
      });
      driverRef.current = driver;
      await driver.connect(data);
    } catch (e) {
      // 503 = non configuré par l'administrateur, 501 = ce fournisseur ne fait
      // pas d'appels depuis le navigateur. Dans les deux cas, ce n'est pas une
      // panne : inutile de lancer une boucle de reconnexion.
      if (e?.response?.status === 503 || e?.response?.status === 501) setStatus("unconfigured");
      else {
        // La cause serveur (jeton refusé, clé invalide…) vaut mieux qu'un
        // « connexion impossible » générique : c'est elle que l'utilisateur lit.
        const detail = e?.response?.data?.detail;
        const raison = (typeof detail === "string" ? detail : detail?.detail) || "connexion au service impossible";
        handleSignalLost(raison);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, handleIncoming, handleSignalLost, refreshToken]);

  /**
   * Coupe — ou rétablit — la sonnerie d'appel entrant jouée par le navigateur.
   *
   * Le SDK du fournisseur joue sa PROPRE sonnerie, en plus de celle de la
   * fenêtre d'appel entrant. Couper l'une sans l'autre laisse le poste sonner :
   * le commercial appuie sur « couper la sonnerie » et rien ne se tait, ce qui
   * revient à ne pas avoir de bouton du tout (voir crm/lib/ringtone).
   */
  const muteRingtone = useCallback((m) => driverRef.current?.muteRingtone?.(
    m, { hasIncoming: Boolean(incomingRef.current) }), []);

  // Après une coupure, la liaison est morte : on la détruit et on repart de zéro
  const tryReconnect = useCallback(async () => {
    if (reconnectBusyRef.current || manualOfflineRef.current || !navigator.onLine) return;
    // Jamais pendant un appel : la reconnexion détruit l'appareil.
    if (activeRef.current || incomingRef.current) { scheduleRetry(); return; }
    reconnectBusyRef.current = true;
    try {
      driverRef.current?.destroy?.();
      driverRef.current = null;
      await initialize();
    } finally {
      reconnectBusyRef.current = false;
      if (lostRef.current) setStatus((s) => (s === "ready" ? s : "reconnecting"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialize, scheduleRetry]);
  reconnectFnRef.current = tryReconnect;

  const acceptIncoming = useCallback(() => {
    const call = incomingRef.current;
    if (!call) return false;
    const info = incomingInfo || {};
    activeRef.current = call;
    refRef.current = null;
    answeredRef.current = false;
    setActiveInfo({
      direction: "ENTRANT", number: info.number,
      company_id: info.company?.id, company_name: info.company?.nom,
      contact_id: info.contact?.id, company: info.company, contact: info.contact,
    });
    bindCall(call);
    call.accept();
    incomingRef.current = null;
    setIncomingInfo(null);
    return true;
  }, [incomingInfo, bindCall]);

  const rejectIncoming = useCallback(() => {
    const call = incomingRef.current;
    if (!call) return false;
    call.reject();
    incomingRef.current = null;
    setIncomingInfo(null);
    return true;
  }, []);

  const makeCall = useCallback(async (rawNumber, meta = {}) => {
    if (!driverRef.current || status === "unconfigured") {
      toast.error("Téléphonie non disponible — configurez un fournisseur dans l'administration.");
      return;
    }
    if (status !== "ready") {
      toast.error("Téléphone non connecté. Cliquez sur « Se connecter ».");
      return;
    }
    const number = (rawNumber || "").trim();
    if (!number) return;
    // Format et nombre de chiffres vérifiés ici, avant toute requête à
    // l'opérateur : lui ne répondrait qu'après la sonnerie, par un raccroché.
    const problem = dialProblem(number);
    if (problem) { toast.error(problem, { id: "tw-dial-check" }); return; }
    const ref = `${user?.id || "u"}-${Date.now()}`;
    refRef.current = ref;
    answeredRef.current = false;
    // Repère de temps propre à cet appel : sans lui, le watchdog comparait la
    // sonnerie en cours à la date du dernier enregistrement de l'appareil.
    connectingSinceRef.current = Date.now();
    try {
      const call = await driverRef.current.makeCall(toE164(number), {
        ref,
        company_id: meta.company_id || "", contact_id: meta.contact_id || "",
        commercial_id: user?.id || "", caller_name: user?.name || "",
        // Objectif choisi dans « Préparer mon appel » : il repart au fournisseur,
        // qui le pose sur la session Copilot. Sans ce relais, le copilote
        // suggérait sans savoir ce que le commercial cherchait à obtenir.
        objective: meta.objective || "",
        // Solution à proposer, retenue dans « Préparer mon appel » : même
        // relais, même raison — le copilote doit savoir ce qu'on vend.
        solution_id: meta.solution_id || "",
      });
      activeRef.current = call;
      setActiveInfo({
        direction: "SORTANT", number,
        company_id: meta.company_id, company_name: meta.company_name,
        contact_id: meta.contact_id, objective: meta.objective || "",
        solution_id: meta.solution_id || "",
        // Le Copilot ouvre son WebSocket sur cette référence : c'est le même
        // identifiant que le backend passe au fournisseur comme session_id.
        ref,
      });
      setStatus("connecting");
      bindCall(call);
    } catch (e) {
      toast.error(`Impossible de lancer l'appel : ${e?.message || e}`);
      refRef.current = null;
    }
  }, [status, user, bindCall]);

  // Pendant la connexion d'un appel sortant, le serveur sait ce que fait la
  // branche composée (sonne, occupé, pas de réponse, échec + code) : on le
  // relit toutes les deux secondes et on l'affiche, au lieu d'un « Connexion… ».
  useEffect(() => {
    if (status !== "connecting" || !activeInfo?.ref || activeInfo.direction !== "SORTANT") return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/twilio/calls/live", { params: { ref: activeInfo.ref } });
        if (cancelled || !data?.known) return;
        setDialState((d) => ({ ...(d || {}), call_status: data.call_status, sip_code: data.sip_code,
                               error_code: data.error_code, result: data.result }));
      } catch { /* le SDK reste la source de vérité */ }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(t); };
  }, [status, activeInfo?.ref, activeInfo?.direction]);

  const hangup = useCallback(() => {
    activeRef.current?.disconnect();
    driverRef.current?.disconnectAll?.();
  }, []);

  const toggleMute = useCallback(() => {
    const call = activeRef.current;
    if (!call) return;
    const m = !muted;
    call.mute(m);
    setMuted(m);
  }, [muted]);

  // Mise en attente : seuls les pilotes qui l'offrent exposent hold/resume
  // (Telnyx WebRTC) — le bouton n'apparaît que si la capacité est déclarée.
  // En cas de refus du SDK, l'état revient en arrière plutôt que de mentir.
  const toggleHold = useCallback(() => {
    const call = activeRef.current;
    if (!call?.hold) return;
    const h = !held;
    Promise.resolve(h ? call.hold() : call.resume()).catch(() => setHeld(!h));
    setHeld(h);
  }, [held]);

  const sendDigit = useCallback((d) => { activeRef.current?.sendDigits(String(d)); }, []);

  // Reconnexion explicite : on oublie la préférence hors ligne.
  const goOnline = useCallback(() => {
    persistManualOffline(user?.id, false);
    retryAttemptRef.current = 0;
    stopRetry();
    initialize();
  }, [initialize, user?.id]);

  const goOffline = useCallback(() => {
    manualOfflineRef.current = true;
    persistManualOffline(user?.id, true);
    lostRef.current = false;
    stopRetry();
    hangup();
    driverRef.current?.unregister?.();
    // Statut forcé ici, et pas seulement via l'événement de désinscription :
    // pendant une reconnexion l'appareil est souvent déjà mort, l'événement
    // ne se déclenche donc jamais et l'interface restait bloquée sur
    // « Signal perdu — reconnexion… » sans possibilité d'en sortir.
    setStatus("offline");
    // Le toast d'alerte a une durée de 10 s : sans ce dismiss, il continuait
    // d'annoncer une reconnexion alors que l'utilisateur vient de se déconnecter.
    toast.dismiss("tw-signal");
  }, [hangup, user?.id]);

  const clearEnded = useCallback(() => setEndedCall(null), []);

  // Connexion automatique une fois la téléphonie configurée pour ce compte.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const onNetOffline = () => { if (driverRef.current && !manualOfflineRef.current) handleSignalLost("connexion internet coupée"); };
    const onNetOnline = () => {
      if (lostRef.current && !manualOfflineRef.current) { retryAttemptRef.current = 0; reconnectFnRef.current?.(); }
    };
    // Revenir sur l'onglet vaut demande explicite : on repart d'un palier neuf.
    const onVisible = () => {
      if (document.hidden || !lostRef.current || manualOfflineRef.current) return;
      retryAttemptRef.current = 0;
      stopRetry();
      reconnectFnRef.current?.();
    };
    window.addEventListener("offline", onNetOffline);
    window.addEventListener("online", onNetOnline);
    document.addEventListener("visibilitychange", onVisible);
    // Watchdog : détecte les pertes silencieuses (liaison morte sans événement)
    // et les connexions bloquées.
    const watchdog = setInterval(() => {
      if (manualOfflineRef.current) return;
      // Un appel en cours n'est pas une panne. Pendant la sonnerie d'un appel
      // sortant, le statut reste « connecting » jusqu'au décroché : le
      // watchdog y voyait une connexion qui n'aboutit pas, jouait le bip,
      // détruisait l'appareil pour « reconnecter »… et coupait l'appel.
      if (activeRef.current || incomingRef.current) return;
      const st = statusRef.current;
      const driver = driverRef.current;
      if (st === "ready" && driver && !driver.registered) {
        handleSignalLost("liaison interrompue");
      } else if (st === "connecting" && Date.now() - connectingSinceRef.current > 15000) {
        handleSignalLost("la connexion n'aboutit pas");
      }
    }, 8000);
    api.get("/telephony/status").then(({ data }) => {
      if (cancelled) return;
      setProviderInfo(data);
      // Le softphone n'existe que si le fournisseur actif sait le faire.
      if (!data.configured || data.capabilities?.browser_calls === false) {
        setStatus("unconfigured");
        return;
      }
      if (user.telephony_enabled === false) { setStatus("offline"); return; }
      // Déconnexion volontaire mémorisée : on ne rétablit pas la liaison à chaque
      // chargement de page. L'utilisateur la relance quand il le décide, via
      // « Se connecter » — ce qui efface la préférence.
      if (readManualOffline(user.id)) {
        manualOfflineRef.current = true;
        setStatus("offline");
        return;
      }
      initialize();
    }).catch(() => setStatus("offline"));
    return () => {
      cancelled = true; stopTimer(); stopRetry(); clearInterval(watchdog);
      window.removeEventListener("offline", onNetOffline);
      window.removeEventListener("online", onNetOnline);
      document.removeEventListener("visibilitychange", onVisible);
      // Déconnexion ou changement de compte : on range le téléphone sans
      // alarme — l'avertissement « signal perdu » n'a de sens qu'en session.
      const ancien = driverRef.current;
      driverRef.current = null;
      lostRef.current = false;
      toast.dismiss("tw-signal");
      ancien?.destroy?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Relit fournisseur + capacités sans recharger la page. L'administration
  // l'appelle après un enregistrement, une activation ou une auto-configuration :
  // la barre d'appel apparaît (ou disparaît) tout de suite, au lieu de rester
  // figée sur l'état lu au chargement.
  const refreshProvider = useCallback(async () => {
    try {
      const { data } = await api.get("/telephony/status");
      setProviderInfo(data);
      const disponible = data.configured && data.capabilities?.browser_calls !== false;
      if (!disponible) {
        const ancien = driverRef.current;
        driverRef.current = null;
        ancien?.destroy?.();
        setStatus("unconfigured");
        return;
      }
      if (statusRef.current === "unconfigured" && user?.telephony_enabled !== false
          && !readManualOffline(user?.id)) initialize();
    } catch { /* l'état précédent reste affiché */ }
  }, [initialize, user]);

  const value = {
    status, muted, held, callSeconds, activeInfo, incomingInfo, endedCall,
    dialState, dialLabel: dialLabel(dialState),
    provider: providerInfo?.provider || null,
    label: providerInfo?.label || null,
    capabilities: providerInfo?.capabilities || null,
    makeCall, hangup, toggleMute, toggleHold, muteRingtone, sendDigit,
    acceptIncoming, rejectIncoming,
    goOnline, goOffline, clearEnded, refreshProvider, API, getToken,
  };
  return <TelephonyCtx.Provider value={value}>{children}</TelephonyCtx.Provider>;
}
